import api from "./api";

const createApiTimestamp = (seconds, nanoseconds = 0) => {
  const value = new Date(seconds * 1000 + Math.floor(nanoseconds / 1e6));
  value.seconds = seconds;
  value.nanoseconds = nanoseconds;
  value.toDate = () => new Date(value.valueOf());
  value.toJSON = () => ({
    __type: "timestamp",
    seconds,
    nanoseconds,
  });
  return value;
};

const hydrate = (value) => {
  if (Array.isArray(value)) return value.map(hydrate);
  if (value && typeof value === "object") {
    if (value.__type === "timestamp") {
      return createApiTimestamp(value.seconds, value.nanoseconds);
    }
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, hydrate(item)])
    );
  }
  return value;
};

const paramsWithCursor = (params = {}) => {
  const normalized = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );
  if (normalized.cursor) normalized.cursor = JSON.stringify(normalized.cursor);
  return normalized;
};

const list = async (path, params) => {
  const response = await api.axiosApi.get(path, {
    params: paramsWithCursor(params),
  });
  return hydrate(response.data);
};

const listAll = async (path, params = {}) => {
  const items = [];
  let cursor = null;
  do {
    const page = await list(path, { ...params, limit: 100, cursor });
    items.push(...page.items);
    cursor = page.cursor;
  } while (cursor);
  return { items, cursor: null };
};

const item = async (path) => {
  const response = await api.axiosApi.get(path);
  return hydrate(response.data.item);
};

const resources = {
  catalog: {
    get: async () => hydrate((await api.axiosApi.get("/catalog")).data),
  },
  workers: {
    schedules: async () =>
      hydrate((await api.axiosApi.get("/workers/schedules")).data),
  },
  users: {
    me: () => item("/users/me"),
    get: (uid) => item(`/users/${encodeURIComponent(uid)}`),
    list: (params) => list("/users", params),
    findById: async (id) => {
      const result = await list("/users", { id, limit: 1 });
      return result.items[0] || null;
    },
    register: async (data) =>
      hydrate((await api.axiosApi.post("/users/me", data)).data),
    create: async (data) =>
      hydrate((await api.axiosApi.post("/users", data)).data),
    updateMe: async (data) =>
      hydrate((await api.axiosApi.patch("/users/me", data)).data),
    update: async (uid, data) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/users/${encodeURIComponent(uid)}`,
            data
          )
        ).data
      ),
    remove: async (uid) =>
      hydrate(
        (
          await api.axiosApi.delete(
            `/users/${encodeURIComponent(uid)}`
          )
        ).data
      ),
    setRole: async (uid, role) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/users/${encodeURIComponent(uid)}/role`,
            { role }
          )
        ).data
      ),
    sendPhoneCode: async (phone) =>
      hydrate(
        (
          await api.axiosApi.post("/users/me/phone-verification", { phone })
        ).data
      ),
    confirmPhoneCode: async (phone, code) =>
      hydrate(
        (
          await api.axiosApi.post("/users/me/phone-verification/confirm", {
            phone,
            code,
          })
        ).data
      ),
  },
  tickets: {
    list: (params) => list("/tickets", params),
    get: (id) => item(`/tickets/${encodeURIComponent(id)}`),
    events: (id, params) =>
      list(`/tickets/${encodeURIComponent(id)}/events`, params),
    create: async (data) =>
      hydrate((await api.axiosApi.post("/tickets", data)).data),
    createAnonymous: async (data) =>
      hydrate((await api.axiosApi.post("/tickets/anonymous", data)).data),
    respond: async (id, data) =>
      hydrate(
        (
          await api.axiosApi.post(
            `/tickets/${encodeURIComponent(id)}/responses`,
            data
          )
        ).data
      ),
  },
  appointments: {
    list: (params) => list("/appointments", params),
    availability: async (params) =>
      hydrate(
        (
          await api.axiosApi.get("/appointments/availability", { params })
        ).data
      ),
    create: async (data) =>
      hydrate((await api.axiosApi.post("/appointments", data)).data),
    cancel: async (uid, note = "") =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/appointments/${encodeURIComponent(uid)}/cancel`,
            { note }
          )
        ).data
      ),
  },
  notifications: {
    list: (params) => list("/notifications", params),
    read: async (uid) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/notifications/${encodeURIComponent(uid)}/read`
          )
        ).data
      ),
  },
  departments: {
    list: (params) => params ? list("/departments", params) : listAll("/departments"),
    get: (id) => item(`/departments/${encodeURIComponent(id)}`),
    create: async (data) =>
      hydrate((await api.axiosApi.post("/departments", data)).data),
    update: async (uid, data) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/departments/${encodeURIComponent(uid)}`,
            data
          )
        ).data
      ),
  },
  statuses: {
    list: (params) => params ? list("/statuses", params) : listAll("/statuses"),
    create: async (data) =>
      hydrate((await api.axiosApi.post("/statuses", data)).data),
    update: async (uid, data) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/statuses/${encodeURIComponent(uid)}`,
            data
          )
        ).data
      ),
  },
  services: {
    list: (params) => params ? list("/services", params) : listAll("/services"),
    get: (slug) => item(`/services/${encodeURIComponent(slug)}`),
    update: async (uid, data) =>
      hydrate(
        (
          await api.axiosApi.patch(
            `/services/${encodeURIComponent(uid)}`,
            data
          )
        ).data
      ),
    uploadImage: async (uid, file, onUploadProgress) =>
      hydrate(
        (
          await api.axiosApi.post(
            `/services/${encodeURIComponent(uid)}/image`,
            file,
            {
              headers: { "Content-Type": file.type },
              onUploadProgress,
            }
          )
        ).data
      ),
    removeImage: async (uid) =>
      hydrate(
        (
          await api.axiosApi.delete(
            `/services/${encodeURIComponent(uid)}/image`
          )
        ).data
      ),
  },
};

export default resources;
